import { Component, Input, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {
  ActionSheetController,
  AlertController,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { Storage } from "@ionic/storage";
import { TarifasFirebaseService } from "../services/firebase/tarifas-firebase.service";
import { UsersFirebaseService } from "../services/firebase/users-firebase.service";
import { User } from "../shared/types/user.types";
import { Local } from "../shared/types/pdv.types";
import { Subscription } from "rxjs";
import { AuthProvider } from "@mobile/providers/auth";

@Component({
  selector: "app-enderecos",
  templateUrl: "./enderecos.page.html",
  styleUrls: ["./enderecos.page.scss"],
})
export class EnderecosPage implements OnInit {
  @Input()
  isModalSelect = false;

  constructor(
    private modalCtrl: ModalController,
    private formBuilder: UntypedFormBuilder,
    private storage: Storage,
    private actionSheetCtrl: ActionSheetController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private tarifasService: TarifasFirebaseService,
    private usersService: UsersFirebaseService,
    private authProvider: AuthProvider,
  ) {}
  formEndereco: UntypedFormGroup;
  isSubmitted = false;
  isCadastrarOuEditarEndereco = false;

  user: User;
  locais: Local[] = [];
  enderecoSelecionadoId: string;

  editar = false;
  cadastrar = true;
  idEdit: string; // Usaremos o ID do local para edição
  bairros: any = [];
  private userSubscription: Subscription;

  async ngOnInit() {
    this.formEndereco = this.formBuilder.group({
      nome: ["", [Validators.required, Validators.minLength(2)]],
      rua: ["", [Validators.required, Validators.minLength(5)]],
      numero: ["", Validators.required],
      complemento: [""],
      referencia: [""],
      bairro: ["", Validators.required],
      id: [null],
    });

    await this.loadInitialData();
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  async loadInitialData() {
    const afAuthCurrentUser = await this.authProvider.afAuth.currentUser;
    if (afAuthCurrentUser) {
      const currentUser = await this.storage.get("user");
      if (!currentUser || !currentUser.uid) {
        this.toastCtrl.create({
          message: "Erro ao carregar dados do usuário.",
          color: "danger",
          duration: 3000,
        }).then((t) => t.present());
        this.closeModal();
        return;
      }

      this.userSubscription = this.usersService
        .getUserValueChanges(currentUser.uid)
        .subscribe((user) => {
          this.user = user;
          this.locais = this.sortLocais(user.locais || []);
          this.enderecoSelecionadoId = user.endereco_selecionado_id;
        });
    } else {

      const localAnonimo = await this.storage.get('endereco_anonimo');
      if (localAnonimo) {
        this.locais = this.sortLocais([localAnonimo]);
      }
    }




    this.bairros = await this.tarifasService.getBairros();
  }

  private sortLocais(locais: Local[]): Local[] {
    if (!locais || locais.length === 0) {
      return [];
    }
    const selecionadoIndex = locais.findIndex(
      (l) => l.id === this.user?.endereco_selecionado_id
    );
    if (selecionadoIndex > 0) {
      const [selecionadoItem] = locais.splice(selecionadoIndex, 1);
      locais.unshift(selecionadoItem);
    }
    return locais;
  }

  async cadastrarEndereco() {
    this.isSubmitted = true;
    if (!this.formEndereco.valid) {
      this.toastCtrl
        .create({
          message: "Por favor, preencha todos os campos obrigatórios.",
          color: "warning",
          duration: 3000,
          position: "top",
        })
        .then((t) => t.present());
      return false;
    }

    const formValue = this.formEndereco.value;
    const novoLocal: Local = this.mapFormToLocal(formValue);

    // Se o usuário estiver logado, atualiza no Firebase
    if (this.user) {
      let locaisAtualizados = [...(this.user.locais || [])];

      if (this.cadastrar) {
        novoLocal.id = (Date.now() + Math.random()).toString().replace(".", ""); // Gerando id único
        locaisAtualizados.push(novoLocal);
      } else {
        // Editar
        const index = locaisAtualizados.findIndex((l) => l.id === this.idEdit);
        if (index > -1) {
          locaisAtualizados[index] = {
            ...locaisAtualizados[index],
            ...novoLocal,
          };
        }
      }

      try {
        await this.usersService.updateUser(this.user.uid, {
          locais: locaisAtualizados,
        });
        await this.toastCtrl
          .create({
            message: `Endereço ${
              this.cadastrar ? "cadastrado" : "editado"
            } com sucesso!`,
            duration: 2000,
            color: "success",
            position: "top",
          })
          .then((t) => t.present());

        await this.confirmacaoEnderecoPrincipal(novoLocal);

        this.resetFormState();

        if (this.isModalSelect && this.cadastrar) {
          this.selecionarEndereco(novoLocal);
        }
      } catch (error) {
        this.toastCtrl
          .create({
            message: "Ocorreu um erro ao salvar o endereço.",
            color: "danger",
            duration: 3000,
          })
          .then((t) => t.present());
      }
    } else {
      // Usuário não logado, salva no storage local
      // Por enquanto, vamos apenas salvar o endereço selecionado,
      // já que não há uma lista de endereços para um usuário anônimo.
      novoLocal.id = (Date.now() + Math.random()).toString().replace(".", "");
      await this.storage.set('endereco_anonimo', novoLocal);
      this.locais = [novoLocal];
      this.enderecoSelecionadoId = novoLocal.id;
      this.resetFormState();
      if (this.isModalSelect) {
        this.selecionarEndereco(novoLocal);
      }
    }
  }

  private mapFormToLocal(formValue: any): Local {
    return {
      id: formValue.id,
      local_name: formValue.nome,
      endereco: `${formValue.rua}, ${formValue.numero}${
        formValue.complemento ? " - " + formValue.complemento : ""
      }`,
      bairro: formValue.bairro,
      referencia: formValue.referencia,
    };
  }

  private resetFormState() {
    this.formEndereco.reset();
    this.isSubmitted = false;
    this.cadastrar = true;
    this.editar = false;
    this.idEdit = null;
    if (this.locais.length > 0) {
      this.isCadastrarOuEditarEndereco = false;
    }
  }

  async selecionarEndereco(local: Local) {
    if (this.isModalSelect) {
      await this.modalCtrl.dismiss(local);
    }
  }

  async setEnderecoPadrao(localId: string) {
    await this.usersService.updateUser(this.user.uid, {
      endereco_selecionado_id: localId,
    });
    this.toastCtrl
      .create({
        message: "Endereço principal definido!",
        duration: 1500,
        color: "success",
      })
      .then((t) => t.present());
  }

  editarEndereco(local: Local) {
    this.cadastrar = false;
    this.isCadastrarOuEditarEndereco = true;
    this.editar = true;
    this.idEdit = local.id;

    // Mapeia o objeto Local de volta para o formato do formulário
    const [rua, numero] = local.endereco.split(",");
    const [num, ...complementoParts] = (numero || "").trim().split(" - ");
    const complemento = complementoParts.join(" - ");

    this.formEndereco.patchValue({
      nome: local.local_name,
      rua: rua.trim(),
      numero: num.trim(),
      complemento: complemento.trim(),
      referencia: local.referencia,
      bairro: local.bairro,
      id: local.id,
    });
  }

  async presentActionSheet(local: Local) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Selecione uma ação",
      buttons: [
        {
          text: "Marcar como principal",
          icon: "star",
          handler: () => {
            this.setEnderecoPadrao(local.id);
          },
        },
        {
          text: "Editar",
          icon: "create",
          handler: () => {
            this.editarEndereco(local);
          },
        },
        {
          text: "Excluir",
          role: "destructive",
          icon: "trash",
          handler: () => {
            this.confirmarRemover(local.id);
          },
        },
        {
          text: "Cancelar",
          icon: "close",
          role: "cancel",
          handler: () => {},
        },
      ],
    });
    await actionSheet.present();
  }

  async confirmarRemover(localId: string) {
    const alert = await this.alertCtrl.create({
      header: "Deseja remover o endereço?",
      buttons: [
        {
          text: "Não!",
          role: "cancel",
        },
        {
          text: "Sim!",
          handler: async () => {
            const locaisAtualizados = this.locais.filter(
              (l) => l.id !== localId
            );
            const updateData: Partial<User> = { locais: locaisAtualizados };

            // Se o endereço removido era o principal, define o primeiro da lista como novo principal
            if (this.enderecoSelecionadoId === localId) {
              updateData.endereco_selecionado_id =
                locaisAtualizados.length > 0 ? locaisAtualizados[0].id : "";
            }

            await this.usersService.updateUser(this.user.uid, updateData);

            const toast = await this.toastCtrl.create({
              message: "Endereço excluído com sucesso",
              duration: 2000,
              color: "success",
            });
            toast.present();
          },
        },
      ],
    });

    await alert.present();
  }

  async confirmacaoEnderecoPrincipal(changedLocal: Local) {
    if (this.enderecoSelecionadoId !== changedLocal.id) {
      const confirm = await this.alertCtrl.create({
        header: "Endereço Principal",
        message: "Esse endereço é seu o seu principal?",
        buttons: [
          { text: "Sim", role: "sim" },
          { text: "Não", role: "nao" },
        ],
      });
      await confirm.present();

      const data = await confirm.onDidDismiss();
      if (data.role === "sim") {
        await this.setEnderecoPadrao(changedLocal.id);
      }
    }
  }

  get errorControl() {
    return this.formEndereco.controls;
  }

  async closeModal() {
    await this.modalCtrl.dismiss();
  }
}
